import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, ShieldCheck } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const LAST_UPDATED = '26 July 2026';
const CONTACT_EMAIL = 'contact@bikertechie.com';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-28">
    <h2 className="text-lg font-bold text-white mb-3">{title}</h2>
    <div className="space-y-3 text-sm text-slate-400 leading-relaxed">{children}</div>
  </section>
);

const Table = ({ head, rows }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10 my-4">
    <table className="w-full text-left text-xs min-w-[540px]">
      <thead className="bg-white/5 text-slate-300">
        <tr>
          {head.map((h) => (
            <th key={h} className="px-4 py-3 font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">
        {rows.map((row, i) => (
          <tr key={i} className="align-top">
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-slate-400">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Privacy = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Privacy Policy | BikerTechie',
    description:
      'How BikerTechie collects, uses, stores and shares your personal data, the cookies and analytics we use, and how to exercise your rights under the DPDP Act 2023 and GDPR.',
    path: '/privacy',
  });

  return (
    <div className="min-h-screen bg-[#030712] pt-24 text-white" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Header */}
        <div className="mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-500/10 text-violet-300 text-xs font-semibold uppercase tracking-wider">
            <ShieldCheck size={13} />
            Legal
          </span>
          <h1 className="text-4xl font-extrabold text-white mt-4 mb-3">Privacy Policy</h1>
          <p className="text-slate-400 text-sm">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/20 border border-white/5 p-6 md:p-8 rounded-3xl backdrop-blur-md space-y-10"
        >

          <Section id="who-we-are" title="1. Who we are">
            <p>
              This website is operated by <strong className="text-slate-200">BikerTechie</strong>, a
              technology consulting business based at Siddharth Nagar, Uttar Pradesh 272207, India
              ("BikerTechie", "we", "us"). We are the data fiduciary / data controller responsible
              for the personal data described in this policy.
            </p>
            <p>
              For any privacy question or request, contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-300 hover:text-violet-200 underline underline-offset-2">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </Section>

          <Section id="what-we-collect" title="2. What data we collect">
            <p>We collect only two categories of data: what you submit to us, and what our analytics records as you browse.</p>
            <Table
              head={['Data', 'When it is collected', 'Why']}
              rows={[
                ['Name, email address, phone number', 'When you submit the contact form', 'To reply to your enquiry'],
                ['Service of interest, your message', 'When you submit the contact form', 'To understand and route your enquiry'],
                ['Pages viewed, clicks, referring page, session recordings', 'While you browse, only if you accept analytics', 'To understand how the site is used and improve it'],
                ['Browser type, device type, operating system, approximate location derived from IP address', 'While you browse, only if you accept analytics', 'Aggregate audience and compatibility statistics'],
              ]}
            />
            <p>
              We do <strong className="text-slate-200">not</strong> collect payment card details
              through this website, and we do not knowingly collect data from children under 18.
            </p>
          </Section>

          <Section id="session-recording" title="3. Cookies, analytics and session recording">
            <p>
              We use <strong className="text-slate-200">PostHog</strong> for product analytics. With
              your consent, PostHog sets cookies and may record a replay of your visit — the pages
              you view, your mouse movement, scrolling and clicks.
            </p>
            <p>
              <strong className="text-slate-200">Text you type into form fields is masked before it
              leaves your browser</strong> and is not visible in these recordings. Recording of
              embedded third-party frames (such as the Google Map on our contact page) is disabled.
            </p>
            <p>
              Analytics and session recording are <strong className="text-slate-200">off by
              default</strong>. Nothing is captured until you press "Accept" on the cookie banner,
              and you can withdraw consent at any time — see section 7.
            </p>
            <p>
              Cookies that are strictly necessary for the site to function, and your cookie choice
              itself (stored locally in your browser), do not require consent.
            </p>
          </Section>

          <Section id="third-parties" title="4. Who we share data with">
            <p>We do not sell your personal data. We share it only with the service providers we rely on to run this website:</p>
            <Table
              head={['Provider', 'What it receives', 'Purpose']}
              rows={[
                ['Google (Google Forms)', 'Your contact form submission', 'Receiving and storing enquiries'],
                ['Google (Maps)', 'Your IP address, when the map on our contact page loads', 'Showing our office location'],
                ['Google Cloud Platform', 'Standard server request logs', 'Website hosting (Cloud Run)'],
                ['PostHog (United States)', 'Analytics events and session recordings, if you consent', 'Product analytics'],
                ['MongoDB', 'Enquiry records, where stored by our own systems', 'Database storage'],
              ]}
            />
            <p>
              Our fonts and images are served from our own servers rather than a third-party CDN, so
              loading and reading this site does not disclose your IP address to any font or image
              provider.
            </p>
            <p>
              Some of these providers process data outside India, including in the United States.
              Where that happens we rely on the provider's contractual data-protection commitments.
              We may also disclose data where we are legally required to do so.
            </p>
          </Section>

          <Section id="retention" title="5. How long we keep it">
            <p>
              Contact enquiries are retained for up to <strong className="text-slate-200">24
              months</strong> after our last correspondence with you, so that we can follow up and
              maintain a record of the request. Analytics data and session recordings are retained
              for up to <strong className="text-slate-200">12 months</strong>.
            </p>
            <p>You can ask us to delete your data sooner at any time — see section 7.</p>
          </Section>

          <Section id="security" title="6. How we protect it">
            <p>
              Data is transmitted over encrypted HTTPS connections and stored with the providers
              listed above under their access controls. Access within BikerTechie is limited to the
              people who need it to answer your enquiry. No method of transmission or storage is
              completely secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section id="your-rights" title="7. Your rights">
            <p>
              Under India's <strong className="text-slate-200">Digital Personal Data Protection Act,
              2023</strong>, and — if you are in the European Economic Area or United Kingdom — the{' '}
              <strong className="text-slate-200">GDPR</strong>, you have the right to:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Access the personal data we hold about you, and know who we have shared it with</li>
              <li>Have inaccurate or incomplete data corrected or completed</li>
              <li>Have your data erased when we no longer need it</li>
              <li>Withdraw your consent to analytics at any time, without affecting anything you have already been sent</li>
              <li>Nominate another person to exercise these rights on your behalf in the event of death or incapacity (DPDP Act)</li>
              <li>Restrict or object to processing, and receive your data in a portable format (GDPR)</li>
              <li>Lodge a complaint with the Data Protection Board of India, or your local supervisory authority in the EEA/UK</li>
            </ul>
            <p>
              To withdraw analytics consent, clear this site's data in your browser settings and
              decline the banner when it reappears. To exercise any other right, email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-300 hover:text-violet-200 underline underline-offset-2">
                {CONTACT_EMAIL}
              </a>{' '}
              and we will respond within 30 days.
            </p>
          </Section>

          <Section id="grievance" title="8. Grievance redressal">
            <p>
              As required by the DPDP Act, 2023, you may raise a grievance about our handling of your
              personal data with our grievance officer:
            </p>
            <div className="rounded-2xl bg-slate-950/50 border border-white/5 p-4 text-xs space-y-1.5">
              <p className="text-slate-200 font-semibold">Grievance Officer, BikerTechie</p>
              <p className="flex items-center gap-2">
                <Mail size={13} className="text-violet-400" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-300 hover:text-violet-200 underline underline-offset-2">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p className="text-slate-500">Siddharth Nagar, Uttar Pradesh 272207, India</p>
            </div>
          </Section>

          <Section id="changes" title="9. Changes to this policy">
            <p>
              We may update this policy as our practices or the law change. The revision date at the
              top of this page always reflects the current version. Material changes will be
              highlighted on the site.
            </p>
          </Section>

          <div className="pt-4 border-t border-white/5">
            <button
              onClick={() => navigate('/contact')}
              className="text-sm text-violet-300 hover:text-violet-200 underline underline-offset-2"
            >
              Questions about your data? Get in touch →
            </button>
          </div>

        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;
